import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ArrowLeft, Filter, Search, CheckCircle, Circle, ChevronLeft, ChevronRight, Upload, CheckSquare, Square } from 'lucide-react';
import axios from "axios";

import { API_BASE_URL } from "../api/config";
import API from "../api";
import { showSuccess, showError, showInfo, redirectPage } from "../utils/notification";
import { useAuth } from "../auth/AuthContext";

// Tank Selection Component — list view, filters: District, Block, Department, ULB, Village
function TankSelection({ onTanksSelected, selectedTanks, setSelectedTanks }) {
  // Server-paginated fetch — we never hold all tanks in memory.
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTanks, setCurrentTanks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const tanksPerPage = 50;

  const [filters, setFilters] = useState({
    district: '',
    block: '',
    department: '',
    ulb: '',
    village: '',
    searchTerm: '',
  });

  // Dropdown options come from the dedicated /tanks/filter-options endpoint
  // so we don't have to load 80k rows just to populate selects.
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [ulbs, setUlbs] = useState([]);
  const [villages, setVillages] = useState([]);

  // Map UI filter keys → server query-string keys.
  const buildServerParams = (extra = {}) => {
    const params = {};
    if (filters.district) params.district_name = filters.district;
    if (filters.block) params.block_name = filters.block;
    if (filters.department) params.owned_by = filters.department;
    if (filters.ulb) params.ulb_name = filters.ulb;
    if (filters.village) params.village_or_town_name = filters.village;
    if (filters.searchTerm) params.search = filters.searchTerm.trim();
    return { ...params, ...extra };
  };

  // Bootstrap: load the district + department dropdown options once.
  useEffect(() => {
    API.get('/tanks/filter-options', { params: { field: 'district' } })
      .then((r) => setDistricts(Array.isArray(r?.data?.data) ? r.data.data : []))
      .catch(() => setDistricts([]));
    API.get('/tanks/filter-options', { params: { field: 'department' } })
      .then((r) => setDepartments(Array.isArray(r?.data?.data) ? r.data.data : []))
      .catch(() => setDepartments([]));
  }, []);

  // Refresh dependent dropdowns whenever upstream filter changes.
  useEffect(() => {
    if (filters.district) {
      API.get('/tanks/filter-options', { params: { field: 'block', district_name: filters.district } })
        .then((r) => setBlocks(Array.isArray(r?.data?.data) ? r.data.data : []))
        .catch(() => setBlocks([]));
      API.get('/tanks/filter-options', { params: { field: 'ulb', district_name: filters.district } })
        .then((r) => setUlbs(Array.isArray(r?.data?.data) ? r.data.data : []))
        .catch(() => setUlbs([]));
    } else {
      setBlocks([]);
      setUlbs([]);
    }
  }, [filters.district]);

  useEffect(() => {
    if (filters.block) {
      API.get('/tanks/filter-options', {
        params: { field: 'village', district_name: filters.district, block_name: filters.block },
      })
        .then((r) => setVillages(Array.isArray(r?.data?.data) ? r.data.data : []))
        .catch(() => setVillages([]));
    } else {
      setVillages([]);
    }
  }, [filters.block, filters.district]);

  // Fetch the current page of tanks whenever the page or any filter changes.
  useEffect(() => {
    setLoading(true);
    const params = buildServerParams({ page: currentPage, pageSize: tanksPerPage });
    API.get('/tanks', { params })
      .then((r) => {
        const rows = Array.isArray(r?.data?.data) ? r.data.data : [];
        const total = r?.data?.pagination?.total ?? rows.length;
        setCurrentTanks(rows);
        setTotalCount(total);
      })
      .catch((err) => {
        console.error(err);
        showError(err?.response?.data?.message || 'Failed to load tanks');
        setCurrentTanks([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'district') {
        next.block = '';
        next.ulb = '';
        next.village = '';
      } else if (field === 'block') {
        next.village = '';
      }
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({ district: '', block: '', department: '', ulb: '', village: '', searchTerm: '' });
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / tanksPerPage));
  const startIndex = (currentPage - 1) * tanksPerPage;
  const endIndex = startIndex + currentTanks.length;
  // Tank-level locking has been removed; conflict prevention now happens at the
  // activity level (already-approved activities are greyed in the picker).
  const visibleTanks = currentTanks;
  const filteredTanks = visibleTanks;

  const isSelected = (tank) => !!selectedTanks.find((t) => t.tank_id === tank.tank_id);

  // Add legacy field aliases so the downstream NOCDetails screen keeps working.
  const enrich = (tank) => ({
    ...tank,
    id: tank.tank_id,
    name: tank.water_body_name,
    tank_name: tank.water_body_name,
    location: tank.district_name,
    district: tank.district_name,
    block: tank.block_name,
    village: tank.village_or_town_name,
    ulbName: tank.ulb_name,
    dept: tank.owned_by,
    department: tank.owned_by,
  });

  const handleTankSelection = (tank) => {
    if (isSelected(tank)) {
      setSelectedTanks(selectedTanks.filter((t) => t.tank_id !== tank.tank_id));
    } else {
      setSelectedTanks([...selectedTanks, enrich(tank)]);
    }
  };

  const allOnPageSelected = visibleTanks.length > 0 && visibleTanks.every((t) => isSelected(t));
  const togglePageSelection = () => {
    if (allOnPageSelected) {
      const ids = new Set(visibleTanks.map((t) => t.tank_id));
      setSelectedTanks(selectedTanks.filter((t) => !ids.has(t.tank_id)));
    } else {
      const merged = [...selectedTanks];
      visibleTanks.forEach((t) => {
        if (!isSelected(t)) merged.push(enrich(t));
      });
      setSelectedTanks(merged);
    }
  };

  const handleNext = () => {
    if (selectedTanks.length === 0) {
      showError('Please select at least one tank');
      return;
    }
    onTanksSelected();
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="bg-gray-50 min-h-screen font-['Inter',sans-serif] pt-16 sm:pt-20">
      {/* Breadcrumb Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center py-3">
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-500">Tamil Nadu NOC Portal</span>
              <span className="text-gray-300">→</span>
              <span className="text-gray-500">NGO Dashboard</span>
              <span className="text-gray-300">→</span>
              <span className="text-indigo-600 font-medium">Tank Selection</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4">
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Choose tanks for your NOC application</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            {loading
              ? 'Loading tanks…'
              : `Showing ${totalCount === 0 ? 0 : startIndex + 1}-${Math.min(startIndex + visibleTanks.length, totalCount)} of ${totalCount.toLocaleString('en-IN')} tanks`}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg shadow-sm p-4 mb-4 border border-teal-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 space-y-3 lg:space-y-0">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-indigo-600" />
              <span className="font-semibold text-gray-800">Filters</span>
            </div>
            <button
              onClick={clearFilters}
              className="text-indigo-600 text-sm font-medium hover:text-indigo-700 transition-colors self-start lg:self-auto"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <select
              value={filters.district}
              onChange={(e) => handleFilterChange('district', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            <select
              value={filters.block}
              onChange={(e) => handleFilterChange('block', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Blocks</option>
              {blocks.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>

            <select
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            <select
              value={filters.ulb}
              onChange={(e) => handleFilterChange('ulb', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All ULBs</option>
              {ulbs.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>

            <select
              value={filters.village}
              onChange={(e) => handleFilterChange('village', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Villages</option>
              {villages.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>

            <div className="relative">
              <input
                type="text"
                placeholder="Search tanks..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Selected Summary */}
        {selectedTanks.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-100 border border-indigo-200 rounded-lg p-4 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">{selectedTanks.length}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-indigo-900">Selected ({selectedTanks.length}) tanks</h3>
                  <p className="text-sm text-indigo-700">
                    {selectedTanks.slice(0, 3).map((t) => t.water_body_name || `Tank #${t.tank_id}`).join(', ')}
                    {selectedTanks.length > 3 && ` +${selectedTanks.length - 3} more`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleNext}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Card Grid */}
        <div className="mb-6">
          {/* Toolbar: select-all-on-page + count */}
          {!loading && visibleTanks.length > 0 && (
            <div className="flex items-center justify-between mb-3 px-1">
              <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={togglePageSelection}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span>Select all on this page ({visibleTanks.length})</span>
              </label>
              <span className="text-xs text-gray-500">{selectedTanks.length} selected total</span>
            </div>
          )}

          {loading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
              Loading…
            </div>
          )}

          {!loading && visibleTanks.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
              No tanks found. Try adjusting your filters.
            </div>
          )}

          {!loading && visibleTanks.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {visibleTanks.map((tank) => {
                const selected = isSelected(tank);
                return (
                  <div
                    key={tank.tank_id}
                    onClick={() => handleTankSelection(tank)}
                    className={`relative cursor-pointer rounded-lg border p-3 transition-all shadow-sm hover:shadow-md
                      ${selected
                        ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-300'
                        : 'bg-white border-gray-200 hover:border-indigo-300'}`}
                  >
                    {/* Top-right checkbox */}
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleTankSelection(tank)}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2.5 right-2.5 w-4 h-4 text-indigo-600 rounded"
                    />

                    {/* Tank name */}
                    <h3 className="text-sm font-semibold text-gray-900 pr-6 mb-1 line-clamp-2">
                      {tank.water_body_name || `Tank #${tank.tank_id}`}
                    </h3>

                    {/* Department + type pills */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {tank.owned_by && (
                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded">
                          {tank.owned_by}
                        </span>
                      )}
                      {tank.tank_type && (
                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-700 rounded">
                          {tank.tank_type}
                        </span>
                      )}
                    </div>

                    {/* Location stack */}
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div className="truncate"><span className="text-gray-400">District:</span> {tank.district_name || '-'}</div>
                      <div className="truncate"><span className="text-gray-400">Block:</span> {tank.block_name || '-'}</div>
                      <div className="truncate"><span className="text-gray-400">Village:</span> {tank.village_or_town_name || '-'}</div>
                      {tank.section_name && tank.section_name !== '-' && (
                        <div className="truncate"><span className="text-gray-400">Section:</span> {tank.section_name}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalCount > tanksPerPage && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg shadow-sm p-4 border border-gray-200 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-2 text-sm text-gray-600 text-center sm:text-left">
              <span>Page {currentPage} of {totalPages}</span><span>•</span><span>{totalCount.toLocaleString('en-IN')} total tanks</span>
            </div>
            <div className="flex items-center justify-center sm:justify-end space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Previous</span>
              </button>
              <div className="flex space-x-1">
                {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                  let page;
                  if (totalPages <= 5) page = index + 1;
                  else if (currentPage <= 3) page = index + 1;
                  else if (currentPage >= totalPages - 2) page = totalPages - 4 + index;
                  else page = currentPage - 2 + index;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === page ? 'bg-indigo-600 text-white' : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'}`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// NOC Details Component - Per Tank Configuration
// `editApp` (optional): when present, this component is being used to
// edit + resubmit an existing Draft/Returned/Rejected application. The
// form is pre-filled with the app's activities/remarks, and submit calls
// /applications/:id/resubmit instead of creating a new row.
function NOCDetails({ selectedTanks, onBack, editApp = null }) {
  const { user: authUser } = useAuth();
  const [tankConfigs, setTankConfigs] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Hydrate tankConfigs from the existing application when editing.
  // activity_ids on the DB is a CSV of "<catId>" or "<catId>:<subActivity>".
  // We parse that back into { [catId]: true | [sub1, sub2, ...] }.
  useEffect(() => {
    if (!editApp || !selectedTanks?.length) return;
    const tank = selectedTanks[0]; // resubmit is per-application (one tank)
    const tankId = tank.tank_id || tank.id;
    const tokens = String(editApp.activity_ids || '').split(',').map((s) => s.trim()).filter(Boolean);
    const selectedActivities = {};
    tokens.forEach((tok) => {
      const [cat, sub] = tok.split(':');
      if (sub) {
        if (!Array.isArray(selectedActivities[cat])) selectedActivities[cat] = [];
        selectedActivities[cat].push(sub);
      } else {
        selectedActivities[cat] = true;
      }
    });
    const remarks = String(editApp.remarks || '');
    const lumpsumMatch = remarks.match(/Lumpsum:\s*([0-9.]+)/i);
    setTankConfigs({
      [tankId]: {
        selectedActivities,
        lumpsumAmount: lumpsumMatch ? lumpsumMatch[1] : '',
        // Files can't be pre-populated from URLs — user re-uploads if needed.
        beforePhoto: [],
        projectProposal: null,
        kmlFile: null,
      },
    });
  }, [editApp, selectedTanks]);

  // Activities already APPROVED on each selected tank — these must be
  // disabled in the picker so the new applicant can't apply for them again.
  // Shape: { [tankId]: Set<token> } where token is either "<catId>" (whole
  // category) or "<catId>:<subActivity>".
  const [approvedActivitiesByTank, setApprovedActivitiesByTank] = useState({});

  useEffect(() => {
    if (!Array.isArray(selectedTanks) || selectedTanks.length === 0) return;
    Promise.all(
      selectedTanks.map((t) => {
        const id = t.tank_id || t.id;
        return API.get(`/tanks/${id}/approved-activities`)
          .then((r) => [id, new Set(Array.isArray(r?.data?.data) ? r.data.data : [])])
          .catch(() => [id, new Set()]);
      })
    ).then((pairs) => {
      const map = {};
      for (const [id, set] of pairs) map[id] = set;
      setApprovedActivitiesByTank(map);
    });
  }, [selectedTanks]);

  // Helper: is a given (catId, subActivity) already approved on a given tank?
  const isActivityLocked = (tankId, catId, subActivity = null) => {
    const set = approvedActivitiesByTank[tankId];
    if (!set || set.size === 0) return false;
    if (subActivity) return set.has(`${catId}:${subActivity}`);
    // Whole-category lock (no sub-activities): match the bare category id.
    return set.has(String(catId));
  };

  // FRS Section III + IV: Self-declaration and Terms & Conditions.
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [tncChecks, setTncChecks] = useState({
    tnc1: false, // Government reserves right to suspend/cancel NOC
    tnc2: false, // NOC subject to genuineness of documents
  });
  const allTncChecked = Object.values(tncChecks).every(Boolean);
  const toggleSelectAllTnc = (checked) => {
    setTncChecks(Object.fromEntries(Object.keys(tncChecks).map((k) => [k, checked])));
  };
  const [submitting, setSubmitting] = useState(false);

  // Master data needed to resolve FK ids before submitting an application
  const [masters, setMasters] = useState({
    districts: [],
    departments: [],
    sections: [],
    approvers: [],
  });

  useEffect(() => {
    const safeGet = (url) => API.get(url).then((r) => (Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => []);
    Promise.all([
      safeGet('/districts'),
      safeGet('/departments'),
      safeGet('/sections'),
      safeGet('/application-approvers'),
    ]).then(([districts, departments, sections, approvers]) => {
      setMasters({ districts, departments, sections, approvers });
    });
  }, []);

  // Activities organized by categories with sub-activities
  const activityCategories = [
    {
      id: 1,
      title: 'Desilting/ Deepening',
      subActivities: [
        'Strengthening of bund',
        'Desilting of channel',
        'Formation of island',
        'Excavation of channels'
      ]
    },
    {
      id: 2,
      title: 'Repair/ rehabilitation of hydraulic structure',
      subActivities: [
        'Surplus weir',
        'Sluice -inlet/outlet'
      ]
    },
    {
      id: 3,
      title: 'Removal of obstruction in water bodies',
      subActivities: [
        'Jungle clearance',
        'Removal of water hyacinth/weeds',
        'Removal of garbage / solid waste materials'
      ]
    },
    {
      id: 4,
      title: 'Infrastructure Arrangements',
      subActivities: [
        'Solar lights',
        'Fencing',
        'Watch towers',
        'Plantation',
        'Walking track',
        'Sitting benches',
        'Bathing ghats',
        'Name picking/investment',
        'Watchman shed',
        'Name board/ Sign board',
        'Floating wetland',
        'Park formation'
      ]
    },
    {
      id: 5,
      title: 'Quality checking of water',
      subActivities: []
    },
    {
      id: 6,
      title: 'Five years AMC by any organization',
      subActivities: []
    }
  ];

  const handleActivityToggle = (tankId, categoryId, activity = null) => {
    // Guard: don't allow toggling something that was already approved on this
    // tank by a previous applicant.
    if (isActivityLocked(tankId, categoryId, activity)) return;
    setTankConfigs(prev => ({
      ...prev,
      [tankId]: {
        ...prev[tankId],
        selectedActivities: {
          ...prev[tankId]?.selectedActivities,
          [categoryId]: activity 
            ? (prev[tankId]?.selectedActivities?.[categoryId]?.includes(activity)
                ? prev[tankId].selectedActivities[categoryId].filter(a => a !== activity)
                : [...(prev[tankId]?.selectedActivities?.[categoryId] || []), activity])
            : !(prev[tankId]?.selectedActivities?.[categoryId] === true)
        }
      }
    }));
  };

  const handleAmountChange = (tankId, amount) => {
    setTankConfigs(prev => ({
      ...prev,
      [tankId]: {
        ...prev[tankId],
        lumpsumAmount: amount
      }
    }));
  };

const handleFileUpload = (tankId, files, fileType) => {
  if (fileType === 'beforePhoto') {
    // Handle multiple photo uploads
    const validFiles = [];
    const errors = [];
    
    // Check if total files exceed limit
    const currentFiles = tankConfigs[tankId]?.beforePhoto || [];
    const totalFiles = currentFiles.length + files.length;
    
    if (totalFiles > 5) {
      alert(`Maximum 5 photos allowed. You can add ${5 - currentFiles.length} more photos.`);
      return;
    }
    
    // Validate each file
    Array.from(files).forEach(file => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name} is not an image file`);
        return;
      }
      
      // Check file size (1MB = 1024 * 1024 bytes)
      if (file.size > 1024 * 1024) {
        errors.push(`${file.name} exceeds 1MB limit (${(file.size / (1024 * 1024)).toFixed(2)}MB)`);
        return;
      }
      
      validFiles.push(file);
    });
    
    // Show errors if any
    if (errors.length > 0) {
      alert('Upload errors:\n' + errors.join('\n'));
      if (validFiles.length === 0) return;
    }
    
    // Update state with valid files
    setTankConfigs(prev => ({
      ...prev,
      [tankId]: {
        ...prev[tankId],
        beforePhoto: [...(prev[tankId]?.beforePhoto || []), ...validFiles]
      }
    }));
  } else {
    // Handle single file uploads for other file types
    setTankConfigs(prev => ({
      ...prev,
      [tankId]: {
        ...prev[tankId],
        [fileType]: files
      }
    }));
  }
};
const removePhoto = (tankId, photoIndex) => {
  setTankConfigs(prev => ({
    ...prev,
    [tankId]: {
      ...prev[tankId],
      beforePhoto: prev[tankId]?.beforePhoto?.filter((_, index) => index !== photoIndex) || []
    }
  }));
};
  const handleSubmitClick = () => {
    // Validate all tanks have required data
    for (const tank of selectedTanks) {
      const config = tankConfigs[tank.id];
      if (!config?.selectedActivities || Object.keys(config.selectedActivities).length === 0) {
        alert(`Please select at least one activity for ${tank.name}`);
        return;
      }
      if (!config?.lumpsumAmount || config.lumpsumAmount <= 0) {
        alert(`Please enter a valid amount for ${tank.name}`);
        return;
      }
if (!config?.beforePhoto || config.beforePhoto.length === 0) {
  alert(`Please upload at least one before photo for ${tank.name}`);
  return;
}
      if (!config?.projectProposal) {
        alert(`Please upload project proposal for ${tank.name}`);
        return;
      }
    }
    
    // Show terms modal
    setShowTermsModal(true);
  };

  // Save the application as DRAFT — no approval chain seeded, no SLA clock.
  // Skips the declaration / T&C requirement so applicants can come back later.
  const handleSaveDraft = () => submitApplications('DRAFT');

  const handleFinalSubmit = async () => {
    if (!declarationChecked) {
      showError('Please accept the self-declaration before submitting');
      return;
    }
    if (!allTncChecked) {
      showError('Please accept all Terms & Conditions before submitting');
      return;
    }
    setShowTermsModal(false);
    return submitApplications('PENDING');
  };

  const submitApplications = async (statusToSet) => {

    // Resolve current user from AuthContext (no localStorage).
    const userInfo = authUser || {};
    const userId = userInfo.user_id;
    if (!userId) {
      showError('Your session is invalid. Please log out and log in again.');
      return;
    }

    const norm = (v) => (v === null || v === undefined ? '' : String(v).trim().toLowerCase());

    // Resolve a master row by name (case/whitespace-insensitive); fall back to the
    // first row so we never submit a NULL FK against a NOT NULL column.
    const resolveId = (rows, idKey, nameKey, value) => {
      if (!Array.isArray(rows) || rows.length === 0) return null;
      if (value) {
        const hit = rows.find((r) => norm(r[nameKey]) === norm(value));
        if (hit) return hit[idKey];
      }
      return rows[0][idKey];
    };

    // department_mst.department_name now uses the same short labels as
    // tank.owned_by (HR&CE, WRD, PWD, Corporation, Municipality, …) — so a
    // direct name match resolves the chain. See changeset gky:29.
    // Aliases for the few one-off variants seen in tank data.
    const DEPT_ALIAS = { 'hr&c': 'HR&CE', 'maws': 'MA&WS' };
    const resolveDepartmentId = (rows, ownedBy) => {
      if (!Array.isArray(rows) || rows.length === 0) return null;
      const aliased = DEPT_ALIAS[norm(ownedBy)] || ownedBy;
      const hit = rows.find(
        (r) => norm(r.department_name) === norm(aliased) || norm(r.department_code) === norm(aliased)
      );
      return (hit || rows[0]).department_id;
    };

    // Pick the first approver template for the resolved department; fall back
    // to the first available template.
    const resolveTemplateId = (rows, departmentId) => {
      if (!Array.isArray(rows) || rows.length === 0) return null;
      const hit = rows.find((r) => Number(r.department_id) === Number(departmentId));
      return (hit || rows[0]).template_id;
    };

    const { districts, departments, sections, approvers } = masters;

    if (
      districts.length === 0 ||
      departments.length === 0 ||
      sections.length === 0 ||
      approvers.length === 0
    ) {
      showError(
        'Master data not available (districts/departments/sections/approver templates). Ask an admin to seed them before submitting.'
      );
      return;
    }

    setSubmitting(true);

    try {
      // Edit-mode: hit /applications/:id/resubmit for the existing app,
      // do NOT create new applications.
      if (editApp) {
        const tank = selectedTanks[0];
        const cfg = tankConfigs[tank.tank_id || tank.id] || {};
        const activityTokens = [];
        Object.entries(cfg.selectedActivities || {}).forEach(([catId, val]) => {
          if (val === true) activityTokens.push(String(catId));
          else if (Array.isArray(val)) val.forEach((sub) => activityTokens.push(`${catId}:${sub}`));
        });
        const activity_ids = activityTokens.join(',').slice(0, 255);
        const remarks = cfg.lumpsumAmount ? `Lumpsum: ${cfg.lumpsumAmount}` : '';
        try {
          await API.post(`/applications/${editApp.application_id}/resubmit`, {
            activity_ids, remarks,
            submit: statusToSet === 'PENDING',
          });
          setShowSuccessModal(true);
        } catch (err) {
          showError(err?.response?.data?.message || 'Failed to save');
        } finally {
          setSubmitting(false);
        }
        return;
      }

      // One application_hdr row per selected tank.
      const requests = selectedTanks.map((tank) => {
        const cfg = tankConfigs[tank.tank_id || tank.id] || {};

        const districtId = resolveId(districts, 'district_id', 'district_name', tank.district_name || tank.district);
        const departmentId = resolveDepartmentId(departments, tank.owned_by || tank.dept);
        const sectionId = resolveId(sections, 'section_id', 'section_name', tank.section_name);
        const templateId = resolveTemplateId(approvers, departmentId);

        // Flatten selected activities (object keyed by category id) into a CSV
        // string of "<categoryId>:<subActivity>" entries — fits the existing
        // VARCHAR(255) activity_ids column.
        const activityTokens = [];
        const selectedActivities = cfg.selectedActivities || {};
        Object.entries(selectedActivities).forEach(([catId, val]) => {
          if (val === true) {
            activityTokens.push(String(catId));
          } else if (Array.isArray(val)) {
            val.forEach((sub) => activityTokens.push(`${catId}:${sub}`));
          }
        });
        const activityIds = activityTokens.join(',').slice(0, 255);

        // application_hdr has no file storage today; record the file names
        // in doc_path so the submission isn't lossy. Real uploads will need
        // a multipart endpoint + a documents table.
        const docMeta = {
          beforePhoto: (cfg.beforePhoto || []).map((f) => f?.name).filter(Boolean),
          projectProposal: cfg.projectProposal?.name || null,
          kmlFile: cfg.kmlFile?.name || null,
          lumpsumAmount: cfg.lumpsumAmount || null,
        };

        // Send as multipart so files actually persist on disk via the new
        // applicationUploadMiddleware. Scalar fields go alongside the files.
        const fd = new FormData();
        fd.append('tank_id', String(tank.tank_id || tank.id));
        fd.append('department_id', String(departmentId));
        fd.append('district_id', String(districtId));
        // Send section_name + block_name; backend upserts them into the
        // respective masters and routes per the department's routing_key.
        fd.append('section_id', String(sectionId));
        if (tank.section_name) fd.append('section_name', String(tank.section_name));
        if (tank.block_name) fd.append('block_name', String(tank.block_name));
        fd.append('approval_template_id', String(templateId));
        fd.append('status', statusToSet);
        if (cfg.lumpsumAmount) fd.append('remarks', `Lumpsum: ${cfg.lumpsumAmount}`);
        fd.append('doc_path', JSON.stringify(docMeta));
        fd.append('activity_ids', activityIds);
        fd.append('applied_by', String(userId));
        fd.append('created_by', String(userId));

        (cfg.beforePhoto || []).forEach((f) => {
          if (f instanceof File || f instanceof Blob) fd.append('before_photo', f, f.name);
        });
        if (cfg.projectProposal instanceof File || cfg.projectProposal instanceof Blob) {
          fd.append('project_proposal', cfg.projectProposal, cfg.projectProposal.name);
        }
        if (cfg.kmlFile instanceof File || cfg.kmlFile instanceof Blob) {
          fd.append('kml_file', cfg.kmlFile, cfg.kmlFile.name);
        }

        return API.post('/applications', fd);
      });

      const results = await Promise.allSettled(requests);
      const failures = results.filter((r) => r.status === 'rejected');

      const verb = statusToSet === 'DRAFT' ? 'Saved as draft' : 'Submitted';
      if (failures.length === 0) {
        showSuccess(`${verb} ${results.length} application${results.length > 1 ? 's' : ''} successfully`);
        redirectPage('/dashboard');
      } else if (failures.length === results.length) {
        const msg = failures[0].reason?.response?.data?.message || `Failed to ${statusToSet === 'DRAFT' ? 'save draft' : 'submit applications'}`;
        showError(msg);
      } else {
        showError(`${verb} ${results.length - failures.length} of ${results.length}; ${failures.length} failed`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      showError(error?.response?.data?.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 min-h-screen font-['Inter',sans-serif] pt-16 sm:pt-20">
      {/* Breadcrumb Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center py-3">
            <button onClick={onBack} className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mr-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-500">Tamil Nadu NOC Portal</span>
              <span className="text-gray-300">→</span>
              <span className="text-gray-500">NGO Dashboard</span>
              <span className="text-gray-300">→</span>
              <span className="text-gray-500">Tank Selection</span>
              <span className="text-gray-300">→</span>
              <span className="text-indigo-600 font-medium">NOC Details</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">NOC Application Details</h1>
            <p className="text-gray-600">Complete the form for each selected tank</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50"
              title="Go back and change your tank selection"
            >
              <ArrowLeft className="w-4 h-4" /> Change Tank Selection
            </button>
            <div className="text-left sm:text-right">
              <span className="text-sm text-gray-500">Tanks to configure</span>
              <div className="text-xl font-bold text-indigo-600">{selectedTanks.length}</div>
            </div>
          </div>
        </div>

        <div className="space-y-6 sm:space-y-8">
          {/* Per Tank Configuration */}
          {selectedTanks.map((tank, index) => (
            <div key={tank.id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Tank Header */}
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Tank #{index + 1}: {tank.name}</h3>
                    <p className="text-sm text-gray-600">{tank.location} • {tank.village}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-6">
                {/* Compact Activity Categories */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4">Select Activities</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {activityCategories.filter(category => !(tank.dept === 'WRD' && category.id === 2)).map((category) => (
                      <div key={category.id} className="bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-indigo-300 transition-all duration-200 h-80 flex flex-col">
                        <div className="flex flex-col h-full">
                          {/* Category Header */}
                          <div className="border-b border-gray-100 pb-2 mb-3 flex-shrink-0">
                            <h5 className="font-semibold text-gray-800 text-sm leading-tight">{category.title}</h5>
                          </div>
                          
                          {/* Sub-activities or main category checkbox */}
                          <div className="flex-1 overflow-y-auto min-h-0">
                            {category.subActivities.length > 0 ? (
                              <div className="space-y-1">
                                {category.subActivities.map((subActivity) => {
                                  const isSelected = tankConfigs[tank.id]?.selectedActivities?.[category.id]?.includes(subActivity);
                                  const locked = isActivityLocked(tank.id, category.id, subActivity);
                                  return (
                                    <div
                                      key={subActivity}
                                      onClick={() => !locked && handleActivityToggle(tank.id, category.id, subActivity)}
                                      title={locked ? 'Already approved for this tank under a previous NOC' : ''}
                                      className={`flex items-start space-x-2 p-1.5 rounded transition-colors ${
                                        locked
                                          ? 'bg-gray-100 opacity-60 cursor-not-allowed line-through'
                                          : isSelected
                                            ? 'bg-indigo-50 border border-indigo-200 cursor-pointer'
                                            : 'hover:bg-gray-50 cursor-pointer'
                                      }`}
                                    >
                                      {isSelected && !locked ? (
                                        <CheckSquare className="w-3 h-3 text-indigo-600 mt-0.5 flex-shrink-0" />
                                      ) : (
                                        <Square className={`w-3 h-3 mt-0.5 flex-shrink-0 ${locked ? 'text-gray-300' : 'text-gray-400'}`} />
                                      )}
                                      <span className={`text-xs leading-tight ${
                                        locked
                                          ? 'text-gray-500'
                                          : isSelected
                                            ? 'text-indigo-900 font-medium'
                                            : 'text-gray-700'
                                      }`}>
                                        {subActivity}
                                        {locked && <span className="ml-1 text-[10px] text-gray-400">(already approved)</span>}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              (() => {
                                const catSelected = tankConfigs[tank.id]?.selectedActivities?.[category.id] === true;
                                const locked = isActivityLocked(tank.id, category.id);
                                return (
                                  <div
                                    onClick={() => !locked && handleActivityToggle(tank.id, category.id)}
                                    title={locked ? 'Already approved for this tank under a previous NOC' : ''}
                                    className={`flex items-center space-x-2 p-2 rounded transition-colors ${
                                      locked
                                        ? 'bg-gray-100 opacity-60 cursor-not-allowed line-through'
                                        : catSelected
                                          ? 'bg-indigo-50 border border-indigo-200 cursor-pointer'
                                          : 'hover:bg-gray-50 cursor-pointer'
                                    }`}
                                  >
                                    {catSelected && !locked ? (
                                      <CheckSquare className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                                    ) : (
                                      <Square className={`w-4 h-4 flex-shrink-0 ${locked ? 'text-gray-300' : 'text-gray-400'}`} />
                                    )}
                                    <span className={`text-xs ${
                                      locked
                                        ? 'text-gray-500'
                                        : catSelected
                                          ? 'text-indigo-900 font-medium'
                                          : 'text-gray-700'
                                    }`}>
                                      {locked ? 'Already approved' : 'Select this category'}
                                    </span>
                                  </div>
                                );
                              })()
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Amount and File Uploads - Responsive Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Lumpsum Amount */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Lumpsum Amount * (₹)</label>
                    <input
                      type="number"
                      value={tankConfigs[tank.id]?.lumpsumAmount || ''}
                      onChange={(e) => handleAmountChange(tank.id, e.target.value)}
                      placeholder="Enter amount"
                      className="w-full px-3 py-2 text-sm border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-indigo-50 focus:bg-white transition-all duration-200"
                    />
                  </div>

{/* Before Photos Upload - Multiple */}
<div className="col-span-1 sm:col-span-2 lg:col-span-2">
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    Before Photos * (Max 5 photos, 1MB each)
  </label>
  
  {/* Upload Area */}
  <div className="border-2 border-dashed border-indigo-300 rounded-lg p-3 text-center hover:border-indigo-400 transition-colors bg-indigo-50 hover:bg-indigo-100 mb-3">
    <div className="space-y-2">
      <Upload className="w-5 h-5 text-indigo-400 mx-auto" />
      <div className="text-gray-600 text-xs">
        Upload Photos ({(tankConfigs[tank.id]?.beforePhoto || []).length}/5)
      </div>
    </div>
    <input
      type="file"
      accept="image/*"
      multiple
      onChange={(e) => handleFileUpload(tank.id, e.target.files, 'beforePhoto')}
      className="hidden"
      id={`before-photo-${tank.id}`}
      disabled={(tankConfigs[tank.id]?.beforePhoto || []).length >= 5}
    />
    <label 
      htmlFor={`before-photo-${tank.id}`} 
      className={`mt-2 inline-flex items-center px-3 py-1 border border-indigo-300 rounded text-xs font-medium transition-colors ${
        (tankConfigs[tank.id]?.beforePhoto || []).length >= 5 
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
          : 'text-indigo-700 bg-white hover:bg-indigo-50 cursor-pointer'
      }`}
    >
      {(tankConfigs[tank.id]?.beforePhoto || []).length >= 5 ? 'Maximum Reached' : 'Choose Photos'}
    </label>
  </div>

  {/* Display Uploaded Photos */}
  {tankConfigs[tank.id]?.beforePhoto && tankConfigs[tank.id].beforePhoto.length > 0 && (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-700">
        Uploaded Photos ({tankConfigs[tank.id].beforePhoto.length}/5):
      </div>
      <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
        {tankConfigs[tank.id].beforePhoto.map((photo, index) => (
          <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 rounded p-2">
            <div className="flex-1 min-w-0">
              <div className="text-green-700 font-medium text-xs truncate">
                {photo.name}
              </div>
              <div className="text-xs text-green-600">
                {(photo.size / (1024 * 1024)).toFixed(2)}MB
              </div>
            </div>
            <button
              onClick={() => removePhoto(tank.id, index)}
              className="ml-2 text-red-500 hover:text-red-700 transition-colors"
              title="Remove photo"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  )}
</div>

                  {/* KML File Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Location (Optional)</label>
                    <div className="border-2 border-dashed border-indigo-300 rounded-lg p-3 text-center hover:border-indigo-400 transition-colors bg-indigo-50 hover:bg-indigo-100">
                      {tankConfigs[tank.id]?.kmlFile ? (
                        <div className="space-y-1">
                          <div className="text-green-600 font-medium text-xs truncate">{tankConfigs[tank.id].kmlFile.name}</div>
                          <div className="text-xs text-gray-500">File uploaded</div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="w-5 h-5 text-indigo-400 mx-auto" />
                          <div className="text-gray-600 text-xs">Upload KML</div>
                        </div>
                      )}
                      <input
                        type="file"
                        accept=".kml"
                        onChange={(e) => handleFileUpload(tank.id, e.target.files[0], 'kmlFile')}
                        className="hidden"
                        id={`kml-upload-${tank.id}`}
                      />
                      <label htmlFor={`kml-upload-${tank.id}`} className="mt-2 inline-flex items-center px-3 py-1 border border-indigo-300 rounded text-xs font-medium text-indigo-700 bg-white hover:bg-indigo-50 cursor-pointer transition-colors">
                        Choose File
                      </label>
                    </div>
                  </div>

                  {/* Project Proposal Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project Proposal *</label>
                    <div className="border-2 border-dashed border-indigo-300 rounded-lg p-3 text-center hover:border-indigo-400 transition-colors bg-indigo-50 hover:bg-indigo-100">
                      {tankConfigs[tank.id]?.projectProposal ? (
                        <div className="space-y-1">
                          <div className="text-green-600 font-medium text-xs truncate">{tankConfigs[tank.id].projectProposal.name}</div>
                          <div className="text-xs text-gray-500">File uploaded</div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="w-5 h-5 text-indigo-400 mx-auto" />
                          <div className="text-gray-600 text-xs">Upload Proposal</div>
                        </div>
                      )}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileUpload(tank.id, e.target.files[0], 'projectProposal')}
                        className="hidden"
                        id={`proposal-upload-${tank.id}`}
                      />
                      <label htmlFor={`proposal-upload-${tank.id}`} className="mt-2 inline-flex items-center px-3 py-1 border border-indigo-300 rounded text-xs font-medium text-indigo-700 bg-white hover:bg-indigo-50 cursor-pointer transition-colors">
                        Choose File
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Global Submit / Save Draft buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-6">
            <button
              onClick={handleSaveDraft}
              disabled={submitting}
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 shadow-sm"
            >
              {submitting ? 'Saving…' : 'Save as Draft'}
            </button>
            <button
              onClick={handleSubmitClick}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-base sm:text-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Submit All NOC Applications ({selectedTanks.length} tanks)
            </button>
          </div>
        </div>

        {/* Terms & Conditions Modal */}
        {showTermsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600">
                <h3 className="text-lg font-semibold text-white">Terms & Conditions - NOC for Water Body Management</h3>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="space-y-6 text-sm text-gray-700">

                  {/* FRS Section III: Self-Declaration */}
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-3">Section III — Self-Declaration</h4>
                    <div className="flex items-start space-x-3 bg-gray-50 border border-gray-200 rounded p-3">
                      <input
                        type="checkbox"
                        id="declaration"
                        checked={declarationChecked}
                        onChange={(e) => setDeclarationChecked(e.target.checked)}
                        className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="declaration" className="text-sm text-gray-800 cursor-pointer">
                        I/we declare that the information given above is true to the best of my/our knowledge
                        and belief and no part thereof is false or no material information has been concealed.
                      </label>
                    </div>
                  </div>

                  {/* FRS Section IV: Terms & Conditions with Select All */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-semibold text-gray-900">Section IV — Terms & Conditions</h4>
                      <label className="flex items-center space-x-2 text-xs font-medium text-indigo-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allTncChecked}
                          onChange={(e) => toggleSelectAllTnc(e.target.checked)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span>Select All</span>
                      </label>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3 bg-gray-50 border border-gray-200 rounded p-3">
                        <input
                          type="checkbox"
                          id="tnc1"
                          checked={tncChecks.tnc1}
                          onChange={(e) => setTncChecks((p) => ({ ...p, tnc1: e.target.checked }))}
                          className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <label htmlFor="tnc1" className="text-sm text-gray-800 cursor-pointer">
                          The Government reserves the right to suspend or cancel the NOC and take legal action
                          if any conditions are violated.
                        </label>
                      </div>
                      <div className="flex items-start space-x-3 bg-gray-50 border border-gray-200 rounded p-3">
                        <input
                          type="checkbox"
                          id="tnc2"
                          checked={tncChecks.tnc2}
                          onChange={(e) => setTncChecks((p) => ({ ...p, tnc2: e.target.checked }))}
                          className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <label htmlFor="tnc2" className="text-sm text-gray-800 cursor-pointer">
                          The issuance of the NOC shall be subject to the genuineness and correctness of the
                          documents submitted. In case of submission of false or misleading information, the
                          NOC shall be liable for cancellation without prior notice.
                        </label>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinalSubmit}
                  disabled={!declarationChecked || !allTncChecked || submitting}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                >
                  {submitting ? 'Submitting…' : 'Submit Applications'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-md w-full transform transition-all">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Success!</h3>
                <p className="text-gray-600 mb-6">All {selectedTanks.length} NOC requests have been submitted successfully.</p>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    window.location.href = '/dashboard';
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Parent Component that manages the flow
export default function NOCRequestFlow() {
  const [currentStep, setCurrentStep] = useState('selection');
  const [selectedTanks, setSelectedTanks] = useState([]);

  const handleTanksSelected = () => {
    setCurrentStep('details');
  };

  const handleBackToSelection = () => {
    setCurrentStep('selection');
  };

  return (
    <div>
      {currentStep === 'selection' && (
        <TankSelection
          onTanksSelected={handleTanksSelected}
          selectedTanks={selectedTanks}
          setSelectedTanks={setSelectedTanks}
        />
      )}
      {currentStep === 'details' && (
        <NOCDetails
          selectedTanks={selectedTanks}
          onBack={handleBackToSelection}
        />
      )}
    </div>
  );
}

// Edit-mode wrapper used by /edit-application/:id — loads the app and its
// tank, then hands both to the same NOCDetails component in editApp mode.
export function EditApplicationFlow() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [tanks, setTanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const appRes = await API.get(`/applications/${id}`);
        const application = appRes?.data?.data || appRes?.data;
        if (!application || !application.application_id) {
          setErr('Application not found');
          setLoading(false);
          return;
        }
        // Fetch the tank record for this application.
        const tankRes = await API.get(`/tanks?tank_id=${application.tank_id}`);
        const tankRow = Array.isArray(tankRes?.data?.data)
          ? tankRes.data.data[0]
          : (tankRes?.data?.data || null);
        // Enrich to the same shape TankSelection produces, so downstream
        // components can keep using `tank.id`, `tank.name`, `tank.dept` etc.
        const enrichedTank = tankRow ? {
          ...tankRow,
          id: tankRow.tank_id,
          name: tankRow.water_body_name,
          tank_name: tankRow.water_body_name,
          location: tankRow.district_name,
          district: tankRow.district_name,
          block: tankRow.block_name,
          village: tankRow.village_or_town_name,
          ulbName: tankRow.ulb_name,
          dept: tankRow.owned_by,
          department: tankRow.owned_by,
        } : null;
        setApp(application);
        setTanks(enrichedTank ? [enrichedTank] : []);
      } catch (e) {
        setErr(e?.response?.data?.message || 'Failed to load application');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading application…</div>;
  if (err) return <div className="p-8 text-center text-red-600">{err}</div>;
  if (!app || !tanks.length) return <div className="p-8 text-center text-red-600">Application data unavailable.</div>;

  return (
    <NOCDetails
      selectedTanks={tanks}
      onBack={() => (window.location.href = '/dashboard')}
      editApp={app}
    />
  );
}